import React from 'react';
import { useForm } from 'react-hook-form';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import CheckboxList from '../../../components/ModernFilters/CheckboxList';
import CameraIntegrationTypeList from '../../../components/ModernFilters/cameras/CameraIntegrationTypeList';
import RegionsList from '../../../components/ModernFilters/RegionsList';
import { translate } from '../../../services/translate';
import { CameraType, IntegrationType } from '../../../schemas/cameras';
import { GetCamerasMapParameters } from '../../../services/central-api/cameras';

interface Props {
    setFilters: React.Dispatch<React.SetStateAction<GetCamerasMapParameters>>;
    filters: GetCamerasMapParameters;
    showMapPoi: boolean;
    setShowMapPoi: React.Dispatch<React.SetStateAction<boolean>>;
}

type FiltersForm = {
    types: CameraType[];
    integrationType: IntegrationType[];
    status: ('online' | 'offline')[];
    poi: 'visible'[];
    regions: {
        type: string;
        value: number;
    };
};

export default function MapFilter({ setFilters, filters, showMapPoi, setShowMapPoi }: Props) {
    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            types: filters.types ?? [],
            integrationType: filters.integrationType ?? [],
            status: [
                ...(filters.online ? ['online' as const] : []),
                ...(filters.offline ? ['offline' as const] : []),
            ],
            poi: showMapPoi ? ['visible'] : [],
            regions: {
                type: filters.polygonType ?? '',
                value: filters.polygon ?? 0,
            },
        },
    });

    const handleFilter = (values: FiltersForm) => {
        setFilters({
            ...filters,
            types: values.types,
            integrationType: values.integrationType,
            online: values.status.includes('online'),
            offline: values.status.includes('offline'),
            polygonType: values.regions.type || undefined,
            polygon: values.regions.value !== 0 ? values.regions.value : undefined,
        });
        setShowMapPoi(values.poi.includes('visible'));
    };

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            types: [],
            integrationType: [],
            status: [],
            poi: [],
            regions: { type: '', value: 0 },
        }}
        applyDisabled={form.watch('regions.type') !== '' && form.watch('regions.value') === 0}
        filters={[{
            label: translate('cameraInfoLabel'),
            filters: [{
                componentHandlesCount: false,
                name: 'types',
                label: translate('cameraType'),
                getCount: CheckboxList.getCount,
                component: ({ name }) => <CheckboxList name={name} items={[
                    { label: translate('lpr'), value: 'lpr', tooltipText: translate('lprInfo') },
                    { label: translate('ptz'), value: 'ptz', tooltipText: translate('ptzInfo') },
                    { label: translate('pinned'), value: 'pinned', tooltipText: translate('pinnedInfo') },
                    { label: translate('panoramic'), value: 'panoramic', tooltipText: translate('panoramicInfo') },
                ]} />,
            }, {
                componentHandlesCount: true,
                name: 'integrationType',
                label: translate('integrationType'),
                component: CameraIntegrationTypeList,
            }, {
                componentHandlesCount: false,
                name: 'status',
                label: translate('cameraStatus'),
                getCount: CheckboxList.getCount,
                component: ({ name }) => <CheckboxList name={name} items={[
                    { label: translate('online'), value: 'online' },
                    { label: translate('offline'), value: 'offline' },
                ]} />,
            }, {
                componentHandlesCount: false,
                name: 'poi',
                label: translate('pointsOfInterest'),
                getCount: CheckboxList.getCount,
                component: ({ name }) => <CheckboxList name={name} items={[
                    { label: translate('showPointsOfInterest'), value: 'visible' },
                ]} />,
            }, {
                componentHandlesCount: true,
                name: 'regions',
                label: translate('regions'),
                component: RegionsList,
            }],
        }]}
    />;
}
